import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import QRCode from 'react-qr-code';
import styled, { useTheme } from 'styled-components';

import { Button, Tooltip } from '@column/column-ui-kit';

import { CodeInput, Loading } from '~/components';
import { useReCaptcha } from '~/lib/recaptcha';
import { UserModel } from '~/models';
import { FactorType, MFAStatus, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Box, Grid } from '~/styles';
import { copyToClipboard } from '~/util';

const CodeInputDescription = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const StyledTooltip = styled(Tooltip)`
  display: inline;
  width: 100%;
`;

const VerificationOverview = styled.div`
  display: flex;
  font-size: 14px;
  justify-content: center;
  width: 100%;

  p {
    max-width: 440px;
    margin: 0;
    text-align: center;
    color: ${({ theme }) => theme.secondary.background};
  }
`;

const MfaWrapper = styled.div`
  display: grid;
  grid-template-columns: auto;
  max-width: 360px;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: min-content auto;
  }
`;

const MfaQRCode = styled.div`
  padding: 32px 24px 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 24px;
    border-right: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
  }
`;

const MfaInput = styled.div`
  padding: 24px;
`;

const MfaSetupCode = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-items: start;
  }

  p {
    margin: 0 0 2px;
    font-size: 12px;
    line-height: 16px;
    color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  }
`;

const StyledBox = styled(Box)`
  align-items: center;
  display: flex;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: flex-start;
  }
`;

interface ActionInit {
  action: 'Init';
}

interface ActionStartFactorSetup {
  action: 'StartFactorSetup';
  user: UserModel;
}

interface ActionCompleteFactorSetup {
  action: 'CompleteFactorSetup';
  factorId: string;
  otpauthUri: string;
}

interface ActionStartFactorVerification {
  action: 'StartFactorVerification';
}

interface ActionFailFactorVerification {
  action: 'FailFactorVerification';
}

interface ActionCompleteSetup {
  action: 'CompleteSetup';
}

interface ActionError {
  action: 'Error';
  message: string;
}

type MfaAction =
  | ActionInit
  | ActionStartFactorSetup
  | ActionCompleteFactorSetup
  | ActionStartFactorVerification
  | ActionFailFactorVerification
  | ActionCompleteSetup
  | ActionError;

interface StateInit {
  state: 'Init';
}

interface StateAwaitingSetup {
  state: 'AwaitingSetup';
  user: UserModel;
}

interface StateAwaitingCodeInput {
  state: 'AwaitingCodeInput';
  factorId: string;
  otpauthUri: string;
  user: UserModel;
}

interface StateAwaitingVerification {
  state: 'AwaitingVerification';
  factorId: string;
  otpauthUri: string;
  user: UserModel;
}

interface StateComplete {
  state: 'Complete';
}

interface StateError {
  state: 'Error';
  message: string;
}

type MfaSetupState =
  | StateInit
  | StateAwaitingSetup
  | StateAwaitingCodeInput
  | StateAwaitingVerification
  | StateComplete
  | StateError;

const InitState: StateInit = { state: 'Init' };

const mfaSetupReducer = (state: MfaSetupState, action: MfaAction): MfaSetupState => {
  switch (action.action) {
    case 'Init':
      return { state: 'Init' };
    case 'StartFactorSetup':
      return { state: 'AwaitingSetup', user: action.user };
    case 'CompleteFactorSetup':
      if (state.state !== 'AwaitingSetup') {
        return { state: 'Error', message: 'Invalid state' };
      }
      return {
        state: 'AwaitingCodeInput',
        factorId: action.factorId,
        otpauthUri: action.otpauthUri,
        user: state.user,
      };
    case 'StartFactorVerification':
      if (state.state !== 'AwaitingCodeInput') {
        return { state: 'Error', message: 'Invalid state' };
      }
      return {
        state: 'AwaitingVerification',
        factorId: state.factorId,
        otpauthUri: state.otpauthUri,
        user: state.user,
      };
    case 'FailFactorVerification':
      if (state.state !== 'AwaitingVerification') {
        return { state: 'Error', message: 'Invalid state' };
      }
      return {
        state: 'AwaitingCodeInput',
        factorId: state.factorId,
        otpauthUri: state.otpauthUri,
        user: state.user,
      };
    case 'CompleteSetup':
      return { state: 'Complete' };
    case 'Error':
      return { state: 'Error', message: action.message };
  }
};

interface SetupDeviceMFAProps {
  enabled?: boolean;
  onSuccess?: () => void;
  notificationDisplay?: string;
}

export const SetupDeviceMFA: React.FC<SetupDeviceMFAProps> = ({
  enabled = true,
  onSuccess,
  notificationDisplay = 'page',
}) => {
  const { currentUser: user, refetchCurrentUser, setMfaStatus } = useSessionStore();
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();
  const getReCaptchaToken = useReCaptcha();
  const theme = useTheme();

  const [verificationCode, setVerificationCode] = useState<string>('');
  const [mfaSetupState, dispatch] = useReducer(mfaSetupReducer, InitState);
  const [showCopied, setShowCopied] = useState<boolean>(false);

  const setupMfaMutation = useMutation({
    mutationFn: async (userModel: UserModel) => {
      const reCaptchaToken = await getReCaptchaToken();
      return UserRepository.setupMfa(reCaptchaToken, {
        factorName: userModel.email,
        factorType: FactorType.TOTP,
      });
    },
    onError: (error: any) => {
      dispatch({ action: 'Error', message: (error as Error).message });
    },
    onSuccess: (resp: { factorId?: string; otpauthUri?: string }) => {
      const { factorId, otpauthUri: otpauthUriResp } = resp;
      if (!factorId || !otpauthUriResp) {
        dispatch({ action: 'Error', message: 'An error occurred during setup. Please try again.' });
        return;
      }
      dispatch({ action: 'CompleteFactorSetup', factorId, otpauthUri: otpauthUriResp });
    },
  });

  const verifyMfaMutation = useMutation({
    mutationFn: async ({ code, factorId }: { code: string; factorId: string }) => {
      const reCaptchaToken = await getReCaptchaToken();
      return UserRepository.verifyMfa(reCaptchaToken, {
        code,
        factorId,
      });
    },
    onError: (error: any) => {
      let content = (error as Error)?.message ?? 'An error occurred';
      if (content === 'Incorrect login mfa code.') {
        content = 'Invalid verification code';
      }
      addDangerNotification({
        content,
        display: notificationDisplay,
      });
      dispatch({ action: 'FailFactorVerification' });
      setVerificationCode('');
    },
    onSuccess: (resp: { mfaStatus: MFAStatus }) => {
      setMfaStatus(resp.mfaStatus);
      setVerificationCode('');

      addSuccessNotification({
        content: 'Authenticator app enabled',
      });
      dispatch({ action: 'CompleteSetup' });
    },
  });

  const handleCopySetupCode = useCallback((event: React.MouseEvent, otpauthUriParam: string) => {
    event.preventDefault();
    setShowCopied(true);
    copyToClipboard(otpauthUriParam);
    setTimeout(() => {
      setShowCopied(false);
    }, 550);
  }, []);

  useEffect(() => {
    if (enabled && user && mfaSetupState.state === 'Init') {
      dispatch({ action: 'StartFactorSetup', user });
    }
  }, [enabled, user, mfaSetupState.state]);

  useEffect(() => {
    if (mfaSetupState.state === 'AwaitingSetup' && !setupMfaMutation.isPending) {
      setupMfaMutation.mutate(mfaSetupState.user);
    }
    if (mfaSetupState.state === 'AwaitingCodeInput' && verificationCode.length === 6) {
      dispatch({ action: 'StartFactorVerification' });
    }
    if (mfaSetupState.state === 'AwaitingVerification' && !verifyMfaMutation.isPending) {
      verifyMfaMutation.mutate({
        code: verificationCode,
        factorId: mfaSetupState.factorId,
      });
    }
  }, [mfaSetupState, verificationCode, setupMfaMutation, verifyMfaMutation]);

  useEffect(() => {
    if (mfaSetupState.state === 'Error') {
      addDangerNotification({
        content: mfaSetupState.message,
        display: notificationDisplay,
      });
    }
  }, [mfaSetupState, addDangerNotification, notificationDisplay]);

  useEffect(() => {
    if (mfaSetupState.state === 'Complete') {
      refetchCurrentUser().then(() => {
        onSuccess?.();
      });
    }
  }, [mfaSetupState, onSuccess, refetchCurrentUser]);

  const isLoading = mfaSetupState.state === 'Init' || mfaSetupState.state === 'AwaitingSetup';
  const isAwaitingCodeInput =
    mfaSetupState.state === 'AwaitingCodeInput' || mfaSetupState.state === 'AwaitingVerification';
  const isVerifying = mfaSetupState.state === 'AwaitingVerification';
  const otpauthUri =
    mfaSetupState.state === 'AwaitingCodeInput' || mfaSetupState.state === 'AwaitingVerification'
      ? mfaSetupState.otpauthUri
      : undefined;

  return (
    <Loading isLoading={isLoading || !(isAwaitingCodeInput || otpauthUri)}>
      <Grid vertical>
        <VerificationOverview>
          <p>
            Scan the QR code below with your preferred authenticator app. We recommend 1Password, Authy, or Google
            Authenticator.
          </p>
        </VerificationOverview>
        <StyledBox variant="secondary">
          <MfaWrapper>
            <MfaQRCode>
              <QRCode fgColor={theme.foreground} bgColor="transparent" size={150} value={otpauthUri ?? ''} />
            </MfaQRCode>
            <MfaInput>
              <CodeInputDescription>Enter the code generated by your app</CodeInputDescription>
              <CodeInput
                autoFocus
                isLoading={isVerifying}
                placeholder="######"
                type="number"
                value={verificationCode}
                onChange={setVerificationCode}
              />

              <MfaSetupCode>
                <p>If your authenticator doesn't recognize the QR code, copy this setup code</p>
                <StyledTooltip content={'Copied!'} placement={'bottom'} zIndex={100} isOpen={showCopied}>
                  <Button variant="muted" size="tiny" onClick={(e) => handleCopySetupCode(e, otpauthUri ?? '')}>
                    Copy setup code
                  </Button>
                </StyledTooltip>
              </MfaSetupCode>
            </MfaInput>
          </MfaWrapper>
        </StyledBox>
      </Grid>
    </Loading>
  );
};
